import React, { useEffect, useState, useCallback } from "react";
import "./Students.scss";
import { useNavigate, useLocation } from "react-router-dom";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  MoreOutlined,
  SendOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { Table, Button, Input, Modal, Dropdown } from "antd";
import {
  GET_PENDING_INVITES,
  RESEND_INVITE,
  EDIT_LEAD_STUDENT,
  DELETE_LEAD_STUDENT,
} from "../../Utlis/apiPath";
import { getApi, postApi, putApi, deleteApi } from "../../Utlis/apiService";
import debounce from "lodash.debounce";
import Buttons from "../../Components/Buttons/Buttons";
import Loader from "../../Components/Loader/Loader";
import { errorToast, successToast } from "../../Services/ToastHelper";
import RollNumberConfigModal from "../Students/Rollnumberconfigmodal";

const LeadStudents = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isAscending, setIsAscending] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLeadStudent, setSelectedLeadStudent] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editStudentData, setEditStudentData] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  // Debounce function
  const debounceSearch = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
    }, 800),
    []
  );

  useEffect(() => {
    fetchPendingInvites();
  }, [debouncedSearch]);

  useEffect(() => {
    if (location?.state?.studentAdded) {
      // Refresh the list when returning from Add Student page
      fetchPendingInvites();
      // Clear the navigation state so it doesn't retrigger on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state?.studentAdded]);

  const fetchPendingInvites = async () => {
    setIsLoading(true);
    try {
      const params = {
        search: debouncedSearch,
        pageIndex: 1,
        pageSize: 1000,
      };

      const response = await getApi(GET_PENDING_INVITES, { params });
      // Check if response has data property with pending array
      if (response && response.data && response.data.pending && Array.isArray(response.data.pending)) {
        const students = response.data.pending;
              
        const updatedData = students.map((student, index) => ({
          ...student,
          slNo: index + 1,
          key: student.userId || student.id || index,
        }));
        setTableData(updatedData);
      } else {
        console.warn("No data found in response or incorrect structure");
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching pending invites:", error);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInvite = async (record) => {
    setSelectedLeadStudent(record);
    setIsInviteModalVisible(true);
  };

  const handleSendInviteViaEmail = async () => {
    setIsLoading(true);
    try {
      const payload = {
        userId: selectedLeadStudent.userId,
        email: selectedLeadStudent.email,
      };

      const { status, message } = await postApi(RESEND_INVITE, payload);
      if (status === 200) {
        successToast(message || "Invite sent successfully via email");
        setIsInviteModalVisible(false);
        fetchPendingInvites();
      } else {
        errorToast(message || "Failed to send invite");
      }
    } catch (error) {
      console.error("Error sending invite:", error);
      errorToast(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while sending invite"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendInviteViaWhatsApp = () => {
    if (!selectedLeadStudent) return;

    const message = `Hello ${selectedLeadStudent.firstName} ${selectedLeadStudent.lastName}, 

You have been invited to join Yogipur! Please complete your registration using the following details:
Email: ${selectedLeadStudent.email}

We look forward to having you with us!`;

    const whatsappUrl = `https://wa.me/${
      selectedLeadStudent.phoneNumber
    }?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
    setIsInviteModalVisible(false);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debounceSearch(e.target.value);
  };

  const handleEditStudent = (record) => {
    // Navigate to Add Student page in edit mode and pass initial data
    navigate("/add-atudent", {
      state: {
        editMode: true,
        initialData: {
          studentId: record.userId,
          firstName: record.firstName,
          lastName: record.lastName,
          email: record.email,
          phoneNumber: record.phoneNumber,
          countryName: record.countryName,
          city: record.city,
        },
      },
    });
  };

  const handleUpdateStudent = async (updatedData) => {
    setIsLoading(true);
    try {
      const response = await putApi(EDIT_LEAD_STUDENT, updatedData);
      if (response.status === 200) {
        successToast(response.message || "Student updated successfully");
        setIsEditModalVisible(false);
        setEditStudentData(null);
        fetchPendingInvites();
      } else {
        errorToast(response.message || "Failed to update student");
      }
    } catch (error) {
      console.error("Error updating student:", error);
      errorToast(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while updating student"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (record) => {
    setStudentToDelete(record);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;

    setIsLoading(true);
    try {
      const response = await deleteApi(
        `${DELETE_LEAD_STUDENT}?studentId=${studentToDelete.userId}`
      );
      if (response.status === 200) {
        successToast(response.message || "Student deleted successfully");
        setIsDeleteModalVisible(false);
        setStudentToDelete(null);
        fetchPendingInvites();
      } else {
        errorToast(response.message || "Failed to delete student");
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      errorToast(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while deleting student"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Get menu items for dropdown
  const getMenuItems = (record) => [
    {
      key: "invite",
      icon: <SendOutlined />,
      label: "Send Invite",
      onClick: () => handleSendInvite(record),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
      onClick: () => handleEditStudent(record),
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      onClick: () => handleDeleteClick(record),
      danger: true,
    },
  ];

  const leadStudentColumns = [
    {
      title: "Roll. No",
      key: "rollNo",
      render: (_, record) => `${record.rollNo}`,
    },
    {
      title: "Student Name",
      key: "studentName",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    { 
      title: "Email", 
      dataIndex: "email", 
      key: "email",
      render: (email) => (email && email.trim() ? email : "-"),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phoneNumber) => {
        if (!phoneNumber) return "-";
        
        // Remove + and spaces
        let cleanNumber = phoneNumber.replace(/[^\d]/g, "");
        
        // For India: 91 followed by 10 digits (total 12 digits)
        if (cleanNumber.startsWith("91") && cleanNumber.length === 12) {
          const countryCode = cleanNumber.substring(0, 2);
          const phoneDigits = cleanNumber.substring(2);
          return `+${countryCode} - ${phoneDigits}`;
        }
        
        // For other country codes with more than 10 digits
        const match = cleanNumber.match(/^(\d{1,3})(\d{10,})$/);
        if (match) {
          return `+${match[1]} - ${match[2]}`;
        }
        
        // If no country code, just show the phone number as is
        return phoneNumber;
      },
    },
    {
      title: "Country",
      dataIndex: "countryName",
      key: "country",
      render: (text) => (text && text.trim() ? text : "N/A"),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (text) => (text && text.trim() ? text : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            icon={<MoreOutlined />}
            style={{
              border: "2px solid #1890ff",
              borderRadius: "4px",
            }}
          />
        </Dropdown>
      ),
    },
  ];

  const handleAddStudent = () => {
    navigate("/add-atudent");
  };

  const handleStudentAdded = () => {
    fetchPendingInvites();
  };

  return (
    <div className="student">
      {isLoading && <Loader />}
      <div className="student_header">
        <h2>Lead Students</h2>
        <Buttons variant="primary" onClick={handleAddStudent}>
          <PlusOutlined /> Add Student
        </Buttons>
      </div>

      <div className="student_content">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          <Input
            placeholder="Search by Name or Email"
            prefix={<SearchOutlined />}
            value={search}
            onChange={handleSearch}
            style={{ width: "220px" }}
          />
          <Button
            icon={isAscending === true ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
            onClick={() => setIsAscending((prev) => (prev === true ? false : true))}
            title={isAscending === true ? "Sort Z → A" : "Sort A → Z"}
          />
        </div>
      </div>

      <Table
        columns={leadStudentColumns}
        dataSource={
          isAscending === null
            ? tableData
            : [...tableData].sort((a, b) => {
                const nameA = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
                const nameB = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
                return isAscending
                  ? nameA.localeCompare(nameB)
                  : nameB.localeCompare(nameA);
              })
        }
        loading={isLoading}
        pagination={false}
        rowKey="key"
      />

      {/* Edit Student Modal */}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        open={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setStudentToDelete(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsDeleteModalVisible(false);
              setStudentToDelete(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleDeleteStudent}
            loading={isLoading}
          >
            Delete
          </Button>,
        ]}
      >
        {studentToDelete && (
          <p>
            Are you sure you want to delete{" "}
            <strong>
              {studentToDelete.firstName} {studentToDelete.lastName}
            </strong>
            ?
          </p>
        )}
      </Modal>

      {/* Send Invite Modal */}
      <Modal
        title="Send Invite"
        open={isInviteModalVisible}
        onCancel={() => setIsInviteModalVisible(false)}
        footer={null}
        centered
        width={400}
      >
        {selectedLeadStudent && (
          <div style={{ padding: "20px 0" }}>
            <p style={{ marginBottom: "20px", fontSize: "16px" }}>
              Send invitation to{" "}
              <strong>
                {selectedLeadStudent.firstName} {selectedLeadStudent.lastName}
              </strong>
            </p>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              <Button
                type="primary"
                size="large"
                block
                onClick={handleSendInviteViaEmail}
                style={{
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  height: "50px",
                  fontSize: "16px",
                }}
              >
                📧 Send via Email
              </Button>
              <Button
                type="default"
                size="large"
                block
                onClick={handleSendInviteViaWhatsApp}
                style={{
                  backgroundColor: "#25D366",
                  borderColor: "#25D366",
                  color: "white",
                  height: "50px",
                  fontSize: "16px",
                }}
              >
                📱 Send via WhatsApp
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LeadStudents;